import { motion } from "framer-motion";
import { useMemo } from "react";

/**
 * Three tech/AI/IT-themed section dividers:
 *
 *  - "neural"     : neural network with pulsing nodes + animated synaptic lines
 *  - "circuit"    : PCB / circuit board traces with travelling data pulses + chip
 *  - "datastream" : binary + waveform data stream (Matrix/AI feel)
 *
 * Usage: <SectionDivider variant="neural" flip />
 *   `flip` rotates 180° so the divider can sit on either side without mismatch.
 */
export function SectionDivider({ variant = "neural", flip = false, className = "" }) {
  const wrapStyle = flip ? { transform: "rotate(180deg)" } : undefined;
  return (
    <div
      data-testid={`section-divider-${variant}`}
      aria-hidden="true"
      className={`section-divider section-divider-${variant} ${className}`}
      style={wrapStyle}
    >
      {variant === "neural" && <NeuralDivider />}
      {variant === "circuit" && <CircuitDivider />}
      {variant === "datastream" && <DataStreamDivider />}
      {/* Backward-compat aliases used by some pages */}
      {variant === "wave" && <NeuralDivider />}
      {variant === "beam" && <DataStreamDivider />}
    </div>
  );
}

/* -------------------- NEURAL -------------------- */
function NeuralDivider() {
  // Deterministic node layout — looks designed, not random.
  const nodes = useMemo(
    () => [
      { x: 60, y: 30 },
      { x: 180, y: 60 },
      { x: 320, y: 25 },
      { x: 470, y: 55 },
      { x: 600, y: 30 },
      { x: 740, y: 60 },
      { x: 880, y: 25 },
      { x: 1020, y: 55 },
      { x: 1160, y: 30 },
      { x: 1300, y: 60 },
      { x: 1400, y: 30 },
    ],
    []
  );

  const edges = useMemo(() => {
    const e = [];
    for (let i = 0; i < nodes.length - 1; i++) {
      e.push([nodes[i], nodes[i + 1]]);
      if (i < nodes.length - 2) e.push([nodes[i], nodes[i + 2]]); // skip-1 crosslink
    }
    return e;
  }, [nodes]);

  return (
    <div className="relative w-full h-[90px] md:h-[110px] overflow-hidden">
      <svg
        viewBox="0 0 1440 90"
        preserveAspectRatio="none"
        className="block w-full h-full"
      >
        <defs>
          <linearGradient id="neural-edge" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#2563EB" stopOpacity="0" />
            <stop offset="50%" stopColor="#2563EB" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#1D4ED8" stopOpacity="0" />
          </linearGradient>
          <radialGradient id="neural-node" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#60A5FA" />
            <stop offset="80%" stopColor="#2563EB" />
            <stop offset="100%" stopColor="#1E3A8A" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Synaptic lines */}
        {edges.map(([a, b], i) => (
          <motion.line
            key={`e-${i}`}
            x1={a.x}
            y1={a.y}
            x2={b.x}
            y2={b.y}
            stroke="url(#neural-edge)"
            strokeWidth={1}
            initial={{ pathLength: 0, opacity: 0 }}
            whileInView={{ pathLength: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, delay: 0.04 * i, ease: "easeOut" }}
          />
        ))}

        {/* Travelling synapse pulses */}
        {edges.slice(0, 8).map(([a, b], i) => (
          <circle key={`p-${i}`} r="2.5" fill="#60A5FA">
            <animate
              attributeName="cx"
              values={`${a.x};${b.x};${a.x}`}
              dur={`${3 + (i % 4) * 0.6}s`}
              repeatCount="indefinite"
            />
            <animate
              attributeName="cy"
              values={`${a.y};${b.y};${a.y}`}
              dur={`${3 + (i % 4) * 0.6}s`}
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="0;1;0"
              dur={`${3 + (i % 4) * 0.6}s`}
              repeatCount="indefinite"
            />
          </circle>
        ))}

        {/* Nodes with double-ring glow */}
        {nodes.map((n, i) => (
          <g key={`n-${i}`}>
            <circle cx={n.x} cy={n.y} r="9" fill="url(#neural-node)" opacity="0.35" />
            <circle cx={n.x} cy={n.y} r="3.5" fill="#2563EB">
              <animate
                attributeName="r"
                values="3;4.5;3"
                dur={`${2 + (i % 3) * 0.4}s`}
                repeatCount="indefinite"
              />
            </circle>
          </g>
        ))}
      </svg>
    </div>
  );
}

/* -------------------- CIRCUIT -------------------- */
function CircuitDivider() {
  return (
    <div className="relative w-full h-[88px] md:h-[110px] overflow-hidden">
      <svg viewBox="0 0 1440 90" preserveAspectRatio="none" className="block w-full h-full">
        <defs>
          <linearGradient id="circuit-pulse" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#2563EB" stopOpacity="0" />
            <stop offset="50%" stopColor="#60A5FA" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* PCB trace: stepped path that branches up and down */}
        {[
          "M0 45 L120 45 L150 25 L320 25 L350 60 L520 60 L550 30 L760 30 L790 60 L980 60 L1010 30 L1200 30 L1230 55 L1440 55",
          "M0 70 L80 70 L110 50 L260 50 L290 78 L420 78 L450 50 L640 50 L670 78 L880 78 L910 50 L1100 50 L1130 75 L1440 75",
          "M0 20 L60 20 L90 38 L240 38 L270 12 L440 12 L470 38 L660 38 L690 12 L900 12 L930 38 L1120 38 L1150 22 L1440 22",
        ].map((d, i) => (
          <g key={`t-${i}`}>
            <path
              d={d}
              fill="none"
              stroke="#2563EB"
              strokeOpacity={0.35}
              strokeWidth={1.1}
            />
            {/* travelling pulse on the trace */}
            <circle r="3" fill="#60A5FA">
              <animateMotion
                dur={`${4 + i * 1.2}s`}
                repeatCount="indefinite"
                path={d}
                rotate="auto"
              />
              <animate attributeName="opacity" values="0;1;1;0" dur={`${4 + i * 1.2}s`} repeatCount="indefinite" />
            </circle>
          </g>
        ))}

        {/* Chip / IC block in centre */}
        <g transform="translate(660 28)">
          <rect width="120" height="34" rx="4" fill="#fff" stroke="#2563EB" strokeOpacity="0.55" />
          <rect x="6" y="6" width="108" height="22" rx="2" fill="#EFF6FF" />
          <text
            x="60"
            y="22"
            textAnchor="middle"
            fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
            fontSize="10"
            fill="#1D4ED8"
            letterSpacing="2"
          >
            AI · IC
          </text>
          {/* chip pins */}
          {[12, 36, 60, 84, 108].map((x) => (
            <g key={x}>
              <line x1={x} y1="-4" x2={x} y2="0" stroke="#2563EB" strokeWidth="1.2" />
              <line x1={x} y1="34" x2={x} y2="38" stroke="#2563EB" strokeWidth="1.2" />
            </g>
          ))}
        </g>

        {/* Junction solder dots */}
        {[
          [150, 25], [320, 25], [550, 30], [760, 30], [1010, 30], [1200, 30],
          [110, 50], [290, 78], [450, 50], [670, 78], [910, 50], [1130, 75],
          [90, 38], [270, 12], [470, 38], [690, 12], [930, 38], [1150, 22],
        ].map(([x, y], i) => (
          <circle key={`s-${i}`} cx={x} cy={y} r="2.3" fill="#2563EB" />
        ))}
      </svg>
    </div>
  );
}

/* -------------------- DATASTREAM -------------------- */
function DataStreamDivider() {
  // Pre-computed bit columns for a Matrix-like data rain effect.
  const columns = useMemo(
    () =>
      Array.from({ length: 36 }).map((_, i) => ({
        x: (i * 100) / 36 + 1,
        delay: (i % 9) * 0.4,
        speed: 3.4 + (i % 5) * 0.4,
        bits: Array.from({ length: 6 }).map(() => (Math.random() > 0.5 ? 1 : 0)),
      })),
    []
  );

  return (
    <div className="relative w-full h-[90px] md:h-[120px] overflow-hidden bg-gradient-to-b from-blue-50/40 via-white to-blue-50/30">
      {/* binary rain */}
      <div className="absolute inset-0 font-mono text-[10px] md:text-[11px] text-[#2563EB]/55 select-none">
        {columns.map((c, i) => (
          <div
            key={i}
            className="absolute top-0 datastream-col"
            style={{
              left: `${c.x}%`,
              animationDelay: `${c.delay}s`,
              animationDuration: `${c.speed}s`,
            }}
          >
            {c.bits.map((b, j) => (
              <div key={j} style={{ opacity: 1 - j * 0.15 }}>
                {b}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* central data waveform */}
      <svg
        viewBox="0 0 1440 90"
        preserveAspectRatio="none"
        className="absolute inset-0 block w-full h-full"
      >
        <defs>
          <linearGradient id="ds-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#2563EB" stopOpacity="0" />
            <stop offset="40%" stopColor="#2563EB" stopOpacity="1" />
            <stop offset="60%" stopColor="#1D4ED8" stopOpacity="1" />
            <stop offset="100%" stopColor="#1D4ED8" stopOpacity="0" />
            <animate attributeName="x1" values="-0.3;0.6;-0.3" dur="6s" repeatCount="indefinite" />
            <animate attributeName="x2" values="0.7;1.6;0.7" dur="6s" repeatCount="indefinite" />
          </linearGradient>
        </defs>

        {/* central scan line */}
        <line x1="0" y1="45" x2="1440" y2="45" stroke="#2563EB" strokeOpacity="0.18" strokeWidth="1" />

        {/* digital square waveform */}
        <motion.path
          initial={{ pathLength: 0, opacity: 0 }}
          whileInView={{ pathLength: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, ease: "easeOut" }}
          d="M0 45 L80 45 L80 30 L160 30 L160 60 L240 60 L240 30 L320 30 L320 45 L420 45 L420 25 L520 25 L520 60 L620 60 L620 45 L720 45 L720 30 L820 30 L820 60 L900 60 L900 35 L1000 35 L1000 55 L1080 55 L1080 30 L1180 30 L1180 50 L1280 50 L1280 30 L1360 30 L1360 45 L1440 45"
          fill="none"
          stroke="url(#ds-grad)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* travelling cursor */}
        <circle r="3.5" fill="#1D4ED8" filter="url(#ds-glow)">
          <animateMotion
            dur="7s"
            repeatCount="indefinite"
            path="M0 45 L80 45 L80 30 L160 30 L160 60 L240 60 L240 30 L320 30 L320 45 L420 45 L420 25 L520 25 L520 60 L620 60 L620 45 L720 45 L720 30 L820 30 L820 60 L900 60 L900 35 L1000 35 L1000 55 L1080 55 L1080 30 L1180 30 L1180 50 L1280 50 L1280 30 L1360 30 L1360 45 L1440 45"
          />
        </circle>
      </svg>

      {/* top + bottom fade so it blends into sections */}
      <div className="absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-white to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white to-transparent pointer-events-none" />
    </div>
  );
}
